import React, { useRef, useState } from 'react';
import { CSSTransition } from 'react-transition-group';
import cx from 'classnames';
import css from '../dropdown.module.scss';
import { popUpTransitionClasses } from '../../../../../../styles/transitionStyles/transitionStyles';
import { WorkshopModuleSearchTagGroupsBar } from '../search-bar';
import { WorkshopTagsScrollSearchList } from '../list/workshop/WorkshopTagsScrollSearchList';
import useCatchEnterClick from '../../../../../../hooks/useCatchEnterClick';
import { useSelectedModuleGroupTags } from '../../../libs/workshop/useSelectedModuleGroupTags';
import {
	CreateFirstTagPopup,
} from '../../../../../templates/private-template-tag-group-panel/ui/dropdown/create-first-tag';
import {
	TagCreationForm,
} from '../../../../../templates/private-template-tag-group-panel/ui/dropdown/tag-creation-form';
import { useWorkshopModuleGroupTags } from './hooks/useWorkshopModuleGroupTags';
import { useWorkshopModuleTags } from './hooks/useWorkshopModuleTags';
import { ITag } from '../../../../../../entities/module/private-template-module-tag/api/types';
import { TypeOfTags } from '../../../../../modules-modal/private-template-modules-modal/model/types';
import { TagGroupType } from '../../../../../../entities/template/private-template-tag-group/model/types';

interface TagsScrollSearchDropdownProps {
	groupId: string | undefined,
	tagsWithoutGroups: ITag[] | undefined,
	editable?: boolean,
}

/**
 * Выпадающее окно с тегами выбранной группы, поиском по ним и формой создания тега.
 */
export const WorkshopModuleTagGroupModal = React.forwardRef<HTMLDivElement, TagsScrollSearchDropdownProps>(({
	groupId,
	tagsWithoutGroups,
	editable = false,
}, _) => {
	const [search, setSearch] = useState('');
	const containerRef = useRef<HTMLDivElement>(null);

	const { isLoading, tagGroupsData } = useWorkshopModuleGroupTags(groupId);
	const tagsGroups = tagGroupsData ? tagGroupsData.tagGroups : [];
	const {
		allTags,
		tagName,
		filteredTags,
		bindCreatedTag,
		handleCreateTag,
		onTagNameChange,
	} = useWorkshopModuleTags(groupId, tagsGroups, tagsWithoutGroups, search);

	const {
		onSelectTag,
		selectedTags,
		onCleanSelectedGroupTags,
	} = useSelectedModuleGroupTags(TypeOfTags.WORKSHOP_TAGS);

	useCatchEnterClick(handleCreateTag, tagName.length >= 2);

	const hasTags = allTags && allTags.length > 0;

	if (!hasTags && editable) {
		return <CreateFirstTagPopup groupId={groupId} bindCreatedTag={bindCreatedTag} />;
	}

	return (
		<CSSTransition
			timeout={0}
			unmountOnExit
			nodeRef={containerRef}
			in={!!groupId || groupId === TagGroupType.tagsWithoutGroup}
			classNames={popUpTransitionClasses}
		>
			<div className={css.wrapper} ref={containerRef}>
				{
					hasTags
						? (
							<WorkshopModuleSearchTagGroupsBar
								setSearch={setSearch}
								search={search}
								createTag={bindCreatedTag}
							/>
						)
						: <div className={css.not_found}>Тегов не найдено...</div>
				}

				{
					isLoading || !tagGroupsData
						? <div className={css.not_found}>Тегов не найдено...</div>
						: (
							<WorkshopTagsScrollSearchList
								search={search}
								setSearch={setSearch}
								tags={filteredTags || []}
								onSelectTag={onSelectTag}
								currentIds={selectedTags}
								onCreateTag={bindCreatedTag}
								editable={editable}
							/>
						)
				}
				{editable && (
					<TagCreationForm
						tagName={tagName}
						onTagNameChange={onTagNameChange}
						handleCreateTag={handleCreateTag}
					/>
				)}

				{
					hasTags && (
						<div
							className={cx(css.reset_group, { [css.focus]: selectedTags.length > 0 })}
							role="presentation"
							onClick={() => onCleanSelectedGroupTags(groupId)}
						>
							Сбросить группу
						</div>
					)
				}
			</div>
		</CSSTransition>
	);
});
